import { useState } from "react";
import {
    UserIcon,
    BuildingIcon,
    ClipboardCheckIcon,
    BriefcaseIcon,
    CheckCircleIcon,
} from "lucide-react";;
import EmployerRegisterForm, {
    CreateAccountData,
} from "@container/employer-forms/employer-register-form";
import EmployerDetailsForm, {
    EmployerDetailsData,
} from "@container/employer-forms/employer-details-form";
import CompanyProfileForm, {
    CompanyProfileData,
} from "@container/employer-forms/company-profile-form";
import ReviewForm from "@container/employer-forms/review-form";
import SuccessMessage from "@component/success-message";
import { ProgressBar, ProgressStep } from "@component/progress-bar";
import axios from "axios";
import { authGoogleSSO, authLogin, checkHRDomain, registerHR, registerHRotp } from "@service/auth";
import { TokenResponse } from "@react-oauth/google";
import ThankYou from "./register-success-jobseeker";
import { uploadCompanyProfile } from "@service/employer";
import { setCookie } from "@util/cookie";
import { EmployerDomainVerification } from "@type/auth";
import NotificationMessage from "@component/notification-message";
import { blackListDomain } from "@constant/domina";
import BasicLayout from "./layout/basic";
import { Card, CardContent } from "@component/card";
import { Button } from "@component/button";
import RequestJoinTeam from "./employer-forms/request-join-team";
import JoinTeamVerificationV2 from "./employer-forms/join-team-verification-v2";
import { catchErrorLog } from "@util/sentry";

// Define the consolidated form data interface
interface EmployerFormData {
    // only for google auth
    sub?: string
    // Account data
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    confirmPassword?: string;

    // Employer details
    hiringCount: string;
    employeeTypes: string[];
    organizationType: string;
    otherOrganizationType?: string;

    // Company profile
    companyName: string;
    companyDescription: string;
    companyLogo: File | null;
    companyLinkedin: string;
}

export default function EmployerOnboarding() {
    const inviteMemberEmail = sessionStorage.getItem('__INVITE_MEMBER_EMAIL__') || "";
    const [currentStep, setCurrentStep] = useState(1);
    const [showAccountCreatedMessage, setShowAccountCreatedMessage] = useState(false);
    const [isAuthGoogle, setIsAuthGoogle] = useState<boolean>(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [companyLogoFile, setCompanyLogoFile] = useState<File | null>(null);
    const [isDomainExist, setDomainExist] = useState<boolean>(false);
    const [domainVerification, setDomainVerification] = useState<EmployerDomainVerification | undefined>(undefined)
    const [isLoadingDomainVerification, setLoadingDomainVerification] = useState<boolean>(false)
    const [notificationType, setNotificationType] = useState<string>("")
    const [notificationMessage, setNotificationMessage] = useState<string>("")
    const [errorMessage, setErrorMessage] = useState<string>("")
    const [isShowVerifyOTP, setIsShowVerifyOTP] = useState<boolean>(false);
    const [isInviteMemberAccepted, setInviteMemberAccepted] = useState<boolean>(false);

    // Centralized form data state
    const [formData, setFormData] = useState<EmployerFormData>({
        // Account data
        firstName: "",
        lastName: "",
        email: inviteMemberEmail,
        password: "",
        confirmPassword: "",

        // Employer details
        hiringCount: "",
        employeeTypes: [],
        organizationType: "",
        otherOrganizationType: "",

        // Company profile
        companyName: "",
        companyDescription: "",
        companyLogo: null,
        companyLinkedin: ""
    });

    // Update form data with partial changes
    const updateFormData = (updates: Partial<EmployerFormData>) => {
        setFormData(prev => ({
            ...prev,
            ...updates
        }));
    };

    let steps: ProgressStep[] = [
        {
            label: "Create Account",
            isCompleted: currentStep > 1,
            isActive: currentStep === 1,
        },
        {
            label: "Employer Details",
            isCompleted: currentStep > 2,
            isActive: currentStep === 2,
        },
        {
            label: "Company Profile",
            isCompleted: currentStep > 3,
            isActive: currentStep === 3,
        },
        {
            label: "Review",
            isCompleted: currentStep > 4,
            isActive: currentStep === 4,
        },
        {
            label: "Complete",
            isCompleted: currentStep === 5,
            isActive: currentStep === 5,
        },
    ];

    const handleAccountDataSubmit = (data: CreateAccountData) => {
        updateFormData(data);
        handleCheckDomain(data);
        window.scrollTo(0, 0);

        // Store authentication state in session storage
        sessionStorage.setItem("userName", `${data.firstName} ${data.lastName}`);
        sessionStorage.setItem("isAuthenticated", "true");
    };

    const handleEmployerDetailsSubmit = (data: EmployerDetailsData) => {
        updateFormData(data);
        setCurrentStep(3);
        window.scrollTo(0, 0);
    };

    const handleCompanyProfileSubmit = (data: CompanyProfileData, file: File | null) => {
        updateFormData(data);
        setCurrentStep(4);
        window.scrollTo(0, 0);

        if (file) {
            setCompanyLogoFile(file);
            updateFormData({
                ...data,
                companyLogo: file
            })
        }
    };

    const handleCheckDomain = async (data: CreateAccountData) => {
        setNotificationType("");
        setNotificationMessage("")
        setDomainExist(false);
        setDomainVerification(undefined);
        setLoadingDomainVerification(true);
        try {
            const resp = await checkHRDomain(data.email);
            if (resp) {
                if (resp.data.data.domain_exists) {
                    const respRegister = await registerHRotp({
                        work_email: data.email,
                        password: data.password || "",
                        confirm_password: data.confirmPassword || "",
                        first_name: data.firstName,
                        last_name: data.lastName,
                        company_id: resp.data.data.company_id,
                        sub: data.sub || ""
                    })
                    if (respRegister.status === 202) {
                        setIsShowVerifyOTP(resp.data.data.is_email_already_invited)
                        setDomainExist(Boolean(resp.data.data.domain_exists))
                        setDomainVerification({
                            domain_exists: Boolean(resp.data.data.domain_exists),
                            domain: resp.data.data.domain,
                            domain_member_count: Number(resp.data.data.domain_member_count),
                            domain_member_emails: resp.data.data.domain_member_emails || [],
                            company_id: resp.data.data.company_id,
                            company_name: respRegister.data.data.company_name,
                            company_logo: resp.data.data.company_logo,
                            organization_role: resp.data.data.organization_role,
                        });
                    } else {
                        catchErrorLog(respRegister.data.message)
                        setNotificationType("error");
                        setNotificationMessage(respRegister.data.message || "something wrong")
                    }
                }

                if (!resp.data.data.domain_exists) {
                    setCurrentStep(2);
                }
            }

            setErrorMessage("")
            setLoadingDomainVerification(false);
        } catch (error) {
            if (axios.isAxiosError(error) && error.response?.status === 403) {
                const errorMessage =
                    error.response.data?.errors?.[0] ||
                    error.response.data?.message ||
                    "Something wrong. Please try it again!";
                setNotificationType("error");
                setNotificationMessage(errorMessage);
                setLoadingDomainVerification(false);
                return;
            }
            console.error(`Failed to check domain ${error}`);
            setNotificationType("error");
            setNotificationMessage("Something wrong. Please try it again!")
            setLoadingDomainVerification(false);
        }
    }

    const handleReviewSubmit = async () => {
        // Set loading state to true when starting submission
        setIsSubmitting(true);

        try {
            // call registerHR
            const respRegister = await registerHR({
                work_email: formData.email,
                password: formData.password,
                confirm_password: formData.confirmPassword || "",
                company_name: formData.companyName,
                company_size: formData.hiringCount,
                organization_type: formData.organizationType,
                organization_type_other: formData.otherOrganizationType || "",
                first_name: formData.firstName,
                last_name: formData.lastName,
                job_title: "HR",
                employee_type: formData.employeeTypes,
                company_description: formData.companyDescription,
                hiring_needs: "N/A",
                company_linkedin: formData.companyLinkedin,
                hiring_count: formData.hiringCount,
                company_logo: "",
                sub: formData.sub || ""
            });

            if (respRegister.code === 200 || respRegister.code === 201) {
                let respAuth = null;
                if (formData.sub && formData.sub !== "") {
                    respAuth = await authGoogleSSO({
                        email: formData.email,
                        sub: formData.sub || ""
                    })
                } else {
                    respAuth = await authLogin({
                        email: formData.email,
                        password: formData.password,
                    });
                }

                if (respAuth.status === 200) {
                    setCookie("authToken", respAuth.data?.token?.access_token ?? "", import.meta.env.VITE_AUTH_TOKEN_EXPIRED_DAY);
                    if (companyLogoFile) {
                        // Upload the file to server
                        const response = await uploadCompanyProfile(companyLogoFile);
                        if (response.status !== 200) {
                            console.error("failed to upload company profile")
                        }
                    }

                    // Keep loading state true until navigation completes
                    setCurrentStep(5);
                    window.scrollTo(0, 0);
                    setIsSubmitting(false);
                    // Show success message
                    setShowAccountCreatedMessage(true);
                } else {
                    // If login fails, set loading state to false
                    setIsSubmitting(false);
                }
            } else {
                setNotificationType("error")
                setNotificationMessage(respRegister.message || "")
            }

        } catch (error) {
            console.error("Error during submission:", error);
            // Set loading state to false if there's an error
            setIsSubmitting(false);
        }
    };

    const handleGoogleSignup = async (tokenResponse: TokenResponse) => {
        setIsAuthGoogle(true);

        try {
            // First, fetch user profile information from Google
            const userInfoResponse = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
                headers: {
                    Authorization: `Bearer ${tokenResponse.access_token}`,
                },
            });
            if (!userInfoResponse.ok) {
                throw new Error(`Failed to fetch user info: ${userInfoResponse.statusText}`);
            }

            const userInfo = await userInfoResponse.json();
            const isBlacklisted = blackListDomain.some((domain) =>
                userInfo.email.toLowerCase().endsWith(domain)
            );
            const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userInfo.email);
            if (isBlacklisted) {
                setErrorMessage("Please use your work email (must end with @company.com)");
                setIsAuthGoogle(false);
                return
            } else if (!isValidEmail) {
                setErrorMessage("Please enter a valid email address");
                setIsAuthGoogle(false);
                return
            }

            handleCheckDomain({
                sub: userInfo.sub,
                email: userInfo.email,
                firstName: userInfo.given_name,
                lastName: userInfo.family_name,
            })
            updateFormData({
                sub: userInfo.sub,
                email: userInfo.email,
                firstName: userInfo.given_name,
                lastName: userInfo.family_name,
            })
            setIsAuthGoogle(false);
        } catch (error) {
            console.error("Error during Google signup:", error);
            setIsAuthGoogle(false);
        }
    };

    const handleBackToAccountData = () => {
        setCurrentStep(1);
        window.scrollTo(0, 0);
    };

    const handleBackToEmployerDetails = () => {
        setCurrentStep(2);
        window.scrollTo(0, 0);
    };

    const handleBackToCompanyProfile = () => {
        setCurrentStep(3);
        window.scrollTo(0, 0);
    };

    const renderStepIcon = () => {
        switch (currentStep) {
            case 1:
                return (
                    <UserIcon
                        className="h-5 w-5 text-blue-600"
                        data-pol-id="8qnmro"
                        data-pol-file-name="employer-onboarding"
                        data-pol-file-type="page"
                    />
                );
            case 2:
                return (
                    <BriefcaseIcon
                        className="h-5 w-5 text-blue-600"
                        data-pol-id="b1c4my"
                        data-pol-file-name="employer-onboarding"
                        data-pol-file-type="page"
                    />
                );
            case 3:
                return (
                    <BuildingIcon
                        className="h-5 w-5 text-blue-600"
                        data-pol-id="nqjfvh"
                        data-pol-file-name="employer-onboarding"
                        data-pol-file-type="page"
                    />
                );
            case 4:
                return (
                    <ClipboardCheckIcon
                        className="h-5 w-5 text-blue-600"
                        data-pol-id="8x57zw"
                        data-pol-file-name="employer-onboarding"
                        data-pol-file-type="page"
                    />
                );

            default:
                return null;
        }
    };

    const renderStepTitle = () => {
        switch (currentStep) {
            case 1:
                return "Create Account";
            case 2:
                return "Employer Details";
            case 3:
                return "Company Profile";
            case 4:
                return "Review Information";
            default:
                return "";
        }
    };

    const renderStepDescription = () => {
        switch (currentStep) {
            case 1:
                return "Create your account to get started";
            case 2:
                return "Tell us about your hiring needs";
            case 3:
                return "Tell us about your company";
            case 4:
                return "Please review your information before submitting, you may edit these details from your account settings later on.";
            default:
                return "";
        }
    };

    if (domainVerification) {
        steps = [
            {
                label: "Create Account",
                isCompleted: currentStep > 1,
                isActive: currentStep === 1,
            },
            {
                label: "Join Your Team",
                isCompleted: isDomainExist,
                isActive: isDomainExist,
            },
            {
                label: "Complete",
                isCompleted: currentStep === 5,
                isActive: currentStep === 5,
            },
        ]
    }

    return (
        <>
            <BasicLayout disableMinHeight showFooter={false}>
                {(!isDomainExist) && (
                    <div className="w-full max-w-3xl mx-auto py-8 px-4 sm:mb-[96px]">
                        {/* Loading overlay */}
                        {isSubmitting && (
                            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                                <div className="bg-white p-5 rounded-lg shadow-lg text-center">
                                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600 mx-auto mb-4"></div>
                                    <p className="text-lg font-semibold text-gray-700">Processing your submission...</p>
                                    <p className="text-sm text-gray-500 mt-2">Please wait while we set up your account</p>
                                </div>
                            </div>
                        )}

                        {showAccountCreatedMessage && (
                            <SuccessMessage
                                message="Account created successfully!"
                                description="Now let's set up your employer details."
                                onClose={() => setShowAccountCreatedMessage(false)}
                                duration={5000}
                            />
                        )}

                        {notificationType !== "" && (
                            <NotificationMessage
                                type={notificationType}
                                message={notificationMessage}
                                duration={5000}
                                onClose={() => {
                                    setNotificationType("")
                                    setNotificationMessage("")
                                }}
                            />
                        )}

                        <div className="space-y-8">
                            <div className="rounded-lg border bg-white p-6 md:p-8 shadow-sm">
                                <div className="flex items-center gap-2 mb-2">
                                    {renderStepIcon()}
                                    <h2 className="text-xl font-semibold text-gray-900">
                                        {renderStepTitle()}
                                    </h2>
                                </div>
                                <p className="mt-1 text-sm text-gray-500 mb-6">
                                    {renderStepDescription()}
                                </p>
                                {currentStep === 1 ? (
                                    <EmployerRegisterForm
                                        errorMessage={errorMessage}
                                        onSubmit={handleAccountDataSubmit}
                                        initialData={{
                                            firstName: formData.firstName,
                                            lastName: formData.lastName,
                                            email: formData.email,
                                            password: formData.password,
                                            confirmPassword: formData.confirmPassword
                                        }}
                                        onGoogleSignup={handleGoogleSignup}
                                        isLoading={isLoadingDomainVerification}
                                        isAuthGoogleLoading={isAuthGoogle}
                                    />
                                ) : currentStep === 2 ? (
                                    <EmployerDetailsForm
                                        onSubmit={handleEmployerDetailsSubmit}
                                        onBack={handleBackToAccountData}
                                        initialData={{
                                            hiringCount: formData.hiringCount,
                                            employeeTypes: formData.employeeTypes,
                                            organizationType: formData.organizationType,
                                            otherOrganizationType: formData.otherOrganizationType
                                        }}
                                    />
                                ) : currentStep === 3 ? (
                                    <CompanyProfileForm
                                        onSubmit={handleCompanyProfileSubmit}
                                        onBack={handleBackToEmployerDetails}
                                        initialData={{
                                            companyName: formData.companyName,
                                            companyDescription: formData.companyDescription,
                                            companyLogo: formData.companyLogo,
                                            companyLinkedin: formData.companyLinkedin
                                        }}
                                    />
                                ) : currentStep === 4 ? (
                                    <ReviewForm
                                        accountData={{
                                            sub: formData.sub || "",
                                            firstName: formData.firstName,
                                            lastName: formData.lastName,
                                            email: formData.email,
                                            password: formData.password,
                                            hiringCount: formData.hiringCount,
                                            employeeTypes: formData.employeeTypes,
                                            organizationType: formData.organizationType
                                        }}
                                        companyData={{
                                            companyName: formData.companyName,
                                            companyDescription: formData.companyDescription,
                                            companyLogo: formData.companyLogo,
                                            companyLinkedin: formData.companyLinkedin
                                        }}
                                        onEditAccountData={handleBackToAccountData}
                                        onEditEmployerDetails={handleBackToEmployerDetails}
                                        onEditCompanyProfile={handleBackToCompanyProfile}
                                        onSubmit={handleReviewSubmit}
                                    />
                                ) : currentStep === 5 && (
                                    <ThankYou
                                        role="hr"
                                        message="We will get back to you to inform you on the next steps."
                                        isDomainExist={isDomainExist}
                                        company_name={domainVerification?.company_name || ""}
                                        first_name={formData.firstName}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {(!isShowVerifyOTP && isDomainExist) ? (
                    <div className="flex items-center justify-center h-[calc(100vh-57px)]">
                        <RequestJoinTeam
                            userEmail={formData.email}
                            domainVerification={domainVerification}
                        />
                    </div>
                ) : null}

                {(!isInviteMemberAccepted && isShowVerifyOTP && isDomainExist) ? (
                    <div className="flex items-center justify-center h-[calc(100vh-57px)]">
                        <JoinTeamVerificationV2
                            userEmail={formData.email}
                            domainVerification={domainVerification}
                            onVerify={() => {
                                setInviteMemberAccepted(true);
                            }}
                        />
                    </div>
                ) : null}

                {(isShowVerifyOTP && isInviteMemberAccepted && isDomainExist) && (
                    <div className="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 h-[calc(100vh-57px)]">
                        <Card className="w-full max-w-xl">
                            <CardContent className="p-8 text-center">
                                {inviteMemberEmail !== "" ? (
                                    <div className="mb-6">
                                        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                            <CheckCircleIcon className="w-8 h-8 text-green-600" />
                                        </div>
                                        <h1 className="text-2xl font-semibold text-gray-900 mb-2">
                                            Welcome to {domainVerification?.company_name}'s workspace!
                                        </h1>
                                        <p className="text-gray-600">
                                            You've successfully joined the workspace as a{" "}
                                            <strong>{domainVerification?.organization_role === "read" ? "Member" : "Admin"}</strong>.
                                            <br />
                                            You can now start collaborating with your team.
                                        </p>
                                    </div>
                                ) : (
                                    <div className="mb-6">
                                        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                            <CheckCircleIcon className="w-8 h-8 text-green-600" />
                                        </div>
                                        <h1 className="text-2xl font-semibold text-gray-900 mb-2">
                                            Thank you!
                                        </h1>
                                        <p className="text-gray-600">
                                            Your account has been successfully created.
                                            <br />
                                            Head to your dashboard to explore opportunities.
                                        </p>
                                    </div>
                                )}
                                <Button onClick={() => {
                                    sessionStorage.removeItem('__INVITE_MEMBER_EMAIL__');
                                    window.location.href = "/dashboard";
                                }} className="w-full bg-blue-600 hover:bg-blue-700">
                                    Go to Dashboard
                                </Button>
                            </CardContent>
                        </Card>
                    </div>
                )}

            </BasicLayout>

            <div className="w-full fixed bottom-0 left-0 right-0 px-4 sm:px-8 pt-8 pb-8 sm:pb-4 bg-white shadow-sm">
                <ProgressBar currentStep={currentStep} steps={steps} />
            </div>
        </>
    );
}
